import { type DriveFile, type Permission, type PermissionRole } from '@/shared/DriveTypes';
import { adminEmail } from '@/shared/constants';
import { type Contact, type Profile } from '~/utils/types';

export const contactsMenuHeight = 355;
export const driveRoleMap = {
  owner: 'Owner',
  organizer: 'Organizer',
  fileOrganizer: 'Organizer',
  writer: 'Editor',
  commenter: 'Commenter',
  reader: 'Viewer',
} as const satisfies Record<PermissionRole, string>;

export const driveRoleDescriptions = {
  owner: 'Can edit, share, and set permissions',
  organizer: 'Can edit, share, and set permissions',
  fileOrganizer: 'Can edit, share, and set permissions',
  writer: 'Can organize, add and edit',
  commenter: 'Can comment',
  reader: 'Can read only',
} as const satisfies Record<PermissionRole, string>;

export const sharedDriveFileRoleMap = {
  ...driveRoleMap,
  organizer: 'Manager',
  fileOrganizer: 'Content Manager',
} as const satisfies Record<PermissionRole, string>;

export const sharedDriveRoleMap = {
  ...sharedDriveFileRoleMap,
  writer: 'Contributor',
} as const satisfies Record<PermissionRole, string>;

export const sharedDriveRoleDescriptions = {
  ...driveRoleDescriptions,
  writer: 'Can add, edit and share files',
  fileOrganizer: 'Can add, edit, delete and share content',
  organizer: 'Manage content, people and settings',
} as const satisfies Record<PermissionRole, string>;

export const driveRoles = Object.entries(driveRoleMap)
  .filter(([key]) => key !== 'owner' && key !== 'fileOrganizer' && key !== 'organizer')
  .map(([role, label]) => ({
    role,
    label,
    description: driveRoleDescriptions[role as PermissionRole],
  }));

export const sharedDriveRolesAll = Object.entries(sharedDriveRoleMap)
  .filter(([key]) => key !== 'owner')
  .map(([role, label]) => ({
    role,
    label,
    description: sharedDriveRoleDescriptions[role as PermissionRole],
  }));

const sharedDriveRoles = sharedDriveRolesAll.filter((o) => o.role !== 'fileOrganizer' && o.role !== 'organizer');

export const getRoles = (file: DriveFile | undefined, user: Profile | undefined) => {
  const roleMap = file?.driveId
    ? file.id === file.driveId
      ? sharedDriveRoleMap
      : sharedDriveFileRoleMap
    : driveRoleMap;
  const userPermission = file?.permissions?.find((p) => p.type === 'user' && p.emailAddress === user?.email);
  const roles =
    file?.driveId && file.id === file.driveId
      ? userPermission?.role === 'organizer'
        ? sharedDriveRolesAll
        : sharedDriveRoles
      : driveRoles;

  return {
    roles,
    roleMap,
  };
};

export const getPermissionDisabled = (
  file: DriveFile | undefined,
  permission: Permission,
  user: Profile | undefined,
  userPermission: Permission | undefined
) => {
  if (file?.id === file?.driveId && !userPermission) {
    return true;
  }
  if (userPermission && file?.id === file?.driveId && userPermission.role !== 'organizer') {
    return true;
  }
  // Files in shared drives
  if (
    file?.driveId &&
    file.driveId !== file.id &&
    (permission.type === 'user' || permission.type === 'group') &&
    permission.permissionDetails?.[0].permissionType === 'member'
  ) {
    return true;
  }

  if (file?.capabilities?.canEdit === false) return true;

  if (permission.role === 'owner') return true;

  if (
    permission.type === 'user' &&
    (permission.id === permission.emailAddress ||
      permission.emailAddress === adminEmail ||
      permission.emailAddress === user?.email)
  ) {
    return true;
  }

  return false;
};

const maxWidth = 485;
export const widths = Object.entries({
  2: 420,
  3: 440,
  5: 460,
  10: 485,
});
export const getWidth = (l: number) => widths.find(([key]) => l <= key)?.[1] ?? maxWidth;

export const getFilteredContacts = ({
  contacts,
  wikiContacts,
  permissions,
  pendingPermissions,
  value,
}: {
  contacts: Contact[];
  wikiContacts: Contact[];
  permissions: Permission[] | undefined;
  pendingPermissions: Permission[];
  value: string;
}) => {
  const _contacts = contacts
    .concat(wikiContacts)
    .filter((o, i, arr) => arr.findIndex((a) => a.email === o.email) === i);

  const filteredContacts = _contacts
    .filter((contact) => !permissions?.some((p) => 'emailAddress' in p && p.emailAddress === contact.email))
    .filter((contact) => !pendingPermissions.some((p) => 'emailAddress' in p && p.emailAddress === contact.email))
    .filter(
      (contact) =>
        contact.email?.toLowerCase().includes(value.toLowerCase()) ||
        contact.name?.toLowerCase().includes(value.toLowerCase())
    )
    .sort((a, b) => {
      const valueLower = value.toLowerCase();
      const aName = a.name?.toLowerCase() ?? '';
      const bName = b.name?.toLowerCase() ?? '';
      const aEmail = a.email?.toLowerCase() ?? '';
      const bEmail = b.email?.toLowerCase() ?? '';

      const aNameStarts = aName.startsWith(valueLower);
      const bNameStarts = bName.startsWith(valueLower);
      if (aNameStarts && !bNameStarts) return -1;
      if (!aNameStarts && bNameStarts) return 1;

      const aEmailStarts = aEmail.startsWith(valueLower);
      const bEmailStarts = bEmail.startsWith(valueLower);
      if (aEmailStarts && !bEmailStarts) return -1;
      if (!aEmailStarts && bEmailStarts) return 1;

      return 0;
    });

  return filteredContacts;
};

export const getEmailPostfixes = ({ file, wikiContacts }: { file: DriveFile | undefined; wikiContacts: Contact[] }) => {
  const fileContacts: Contact[] =
    file?.permissions
      ?.filter((p): p is Extract<Permission, { emailAddress: string }> => p.type === 'user')
      .map((p) => ({
        id: p.id,
        name: p.displayName,
        email: p.emailAddress,
        photoURL: '',
      })) ?? [];

  const emailPostfixes = wikiContacts
    .concat(fileContacts)
    .map((contact) => contact.email?.split('@')[1] ?? '')
    .filter((emailPostfix, i, arr) => arr.indexOf(emailPostfix) === i);

  return emailPostfixes;
};
